import { useEffect, useState } from 'react';

import { getBrowserApiUrl } from '@/utils/apiEndpoint';

import type Settings from '@/types/Settings';

type SiteSettingsState = {
  data?: Settings;
  error?: Error;
  isLoading: boolean;
};

const INITIAL_STATE: SiteSettingsState = { isLoading: true };

let cachedSettings: Settings | undefined;
let pendingSettings: Promise<Settings> | undefined;

async function fetchSiteSettings() {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 10_000);

  try {
    const response = await fetch(getBrowserApiUrl('settings/site_'), {
      credentials: 'include',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Site settings request failed with ${response.status}`);
    }

    const payload = (await response.json()) as { data?: Settings };

    if (!payload.data) {
      throw new Error('Site settings response did not include data');
    }

    return payload.data;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('Site settings request timed out', { cause: error });
    }

    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

function loadSiteSettings() {
  if (cachedSettings) {
    return Promise.resolve(cachedSettings);
  }

  pendingSettings ??= fetchSiteSettings()
    .then((settings) => {
      cachedSettings = settings;
      return settings;
    })
    .finally(() => {
      pendingSettings = undefined;
    });

  return pendingSettings;
}

export default function useSiteSettings() {
  const [state, setState] = useState<SiteSettingsState>(() =>
    cachedSettings ? { data: cachedSettings, isLoading: false } : INITIAL_STATE,
  );

  useEffect(() => {
    let isActive = true;

    void loadSiteSettings().then(
      (data) => {
        if (isActive) setState({ data, isLoading: false });
      },
      (error: unknown) => {
        if (!isActive) return;
        setState({
          error: error instanceof Error ? error : new Error(String(error)),
          isLoading: false,
        });
      },
    );

    return () => {
      isActive = false;
    };
  }, []);

  return state;
}
